import readNDJSONStream from "ndjson-readablestream";
import { useState } from "react";

import { chatPostApi, chatPutApi } from "../api/endpoints/chatAPI";
import { ChatEvent } from "../api/types/chatStreamTypes";
import { ChatExchange, ChatPostRequest, ChatPutRequest, LLMResponse } from "../api/types/chatTypes";
import { useConversation } from "../contexts/conversationContext";

/**
 * Custom hook for managing chat data and API requests.
 *
 * @returns An object containing loading state, error, and functions to make API requests, retry, and clear chat.
 */
const useChatData = () => {
    const {
        chatHistory,
        setChatHistory,
        setActiveCitation,
        setIsAnalysisPanelTabActive,
        sessionId,
        aiSearchTime,
        setAiSearchTime,
        embeddingTime,
        setEmbeddingTime,
        setFoundSources
    } = useConversation();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<unknown>();

    /** Initialize an empty AskResponse */
    const createEmptyAskResponse = (): LLMResponse => ({
        message_id: undefined,
        response_id: undefined,
        answer: "",
        answerTime: undefined,
        responseStatus: "loading",
        toolCallStatus: undefined,
        error: undefined
    });

    const appendExchange = (exchange: ChatExchange) => {
        setChatHistory(prev => [...prev, exchange]);
    };

    const patchLastResponse = (response: LLMResponse) => {
        setChatHistory(prev => {
            const list = prev ?? [];
            if (!list.length) return list;
            const last = list[list.length - 1];
            const updated = { ...last, response };
            return [...list.slice(0, -1), updated];
        });
    };

    const getLastMessageResponseId = () => {
        for (let i = chatHistory.length - 1; i >= 0; i--) {
            const resp = chatHistory[i]?.response;
            if (resp?.responseStatus === "completed" && resp.response_id) return resp.response_id;
        }
        return undefined;
    };

    const getLastRetryMessageResponseId = () => {
        for (let i = chatHistory.length - 2; i >= 0; i--) {
            const resp = chatHistory[i]?.response;
            if (resp?.responseStatus === "completed" && resp.response_id) return resp.response_id;
        }
        return undefined;
    };

    /** Handle incoming streaming event */
    function handleEvent(event: ChatEvent, askResponse: LLMResponse): LLMResponse {
        let updated = { ...askResponse };

        if ("annotated_text" in event) {
            updated.answer = event.annotated_text;
            const currentAnswerTime = parseFloat(event.answer_time.toFixed(2));
            const embeddigTimeNumber = embeddingTime ? parseFloat(embeddingTime) : 0;
            const aiSearchTimeNumber = aiSearchTime ? parseFloat(aiSearchTime) : 0;
            const totalTime = currentAnswerTime + embeddigTimeNumber + aiSearchTimeNumber;
            updated.answerTime = `${totalTime.toFixed(2).toString()} s`;
            updated.message_id = event.message_id;
            updated.usedCitations = event.used_citations;
            updated.unusedCitations = event.unused_citations;
            updated.response_id = event.response_id;
            updated.responseStatus = "completed";
        } else if ("status" in event) {
            switch (event.status) {
                case "response.created":
                    updated.response_id = event.response_id;
                    updated.responseStatus = "created";
                    break;
                case "response.in_progress":
                    updated.responseStatus = "in_progress";
                    break;
                case "response.queued":
                    updated.responseStatus = "queued";
                    break;
                case "stopped":
                    updated.responseStatus = "stopped";
                    break;
                case "tool_call.started":
                    updated.toolCallStatus = "started";
                    break;
                case "tool_call.finished":
                    updated.toolCallStatus = "finished";
                    break;
                case "ai_search.finished":
                    setAiSearchTime(event.duration);
                    const foundSources: number = event.results ?? 0;
                    setFoundSources(foundSources);
                    break;
                case "embedding.finished":
                    setEmbeddingTime(event.duration);
                    break;
            }
        } else if ("delta" in event) updated.answer += event.delta;
        else if ("error" in event) updated.error = event.error;

        return updated;
    }

    const APICall = async (question: string, askResponse: LLMResponse, lastMessageResponseId: string | undefined) => {
        setAiSearchTime(undefined);
        setEmbeddingTime(undefined);
        setFoundSources(undefined);
        if (error) setError(undefined);
        setIsLoading(true);

        try {
            const request = lastMessageResponseId
                ? ({ question, session_id: sessionId, previous_message_id: lastMessageResponseId } as ChatPutRequest)
                : ({ question, session_id: sessionId } as ChatPostRequest);

            const response = lastMessageResponseId ? await chatPutApi(request as ChatPutRequest) : await chatPostApi(request as ChatPostRequest);

            for await (const data of readNDJSONStream(response)) {
                askResponse = handleEvent(data as ChatEvent, askResponse);
                patchLastResponse(askResponse);
            }

            patchLastResponse(askResponse);
        } catch (e) {
            setError(e);
            const askResponse = createEmptyAskResponse();
            askResponse.responseStatus = "error";
            patchLastResponse(askResponse);
        } finally {
            setIsLoading(false);
        }
    };

    /** Start a new chat request */
    const makeApiRequest = async (question: string) => {
        const lastMessageResponseId = getLastMessageResponseId();

        const askResponse = createEmptyAskResponse();
        const exchange: ChatExchange = { userMessage: question, response: askResponse };

        appendExchange(exchange);
        await APICall(question, askResponse, lastMessageResponseId);
    };

    const retry = async () => {
        const lastExchange = chatHistory[chatHistory.length - 1];
        const question = lastExchange.userMessage;

        const lastMessageResponseId = getLastRetryMessageResponseId();

        const askResponse = createEmptyAskResponse();
        patchLastResponse(askResponse);

        await APICall(question, askResponse, lastMessageResponseId);
    };

    /**
     * Clears the chat history and resets related state.
     */
    const clearChat = () => {
        if (error) setError(undefined);
        setActiveCitation(undefined);
        setIsAnalysisPanelTabActive(false);
        setChatHistory([]);
    };

    return { isLoading, error, makeApiRequest, retry, clearChat };
};

export default useChatData;
